import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Link,
  Typography,
  makeStyles,
} from "@material-ui/core";
import React from "react";

import { FileUploadIcon } from "../../components/icons/themes/materialdesign";
import useFetchBAA from "../queries/useFetchBAA";
import { WebTrackerSupportsBAA } from "../types";
import { UploadToCloudIcon } from "../../components/icons/themes/windows10";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(2, 2),
  },
  cardActions: {
    justifyContent: "flex-end",
    display: "flex",
    padding: theme.spacing(2, 2),
  },
  loaderWrapper: {
    position: "relative",
    width: "100%",
    minHeight: "200px",
    marginTop: 14,
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
}));

type ViewBAACardProps = {
  onClickDownload: () => void;
  onClickUpload: () => void;
};

function ViewBAACard({ onClickDownload, onClickUpload }: ViewBAACardProps) {
  const classes = useStyles();

  return (
    <Card>
      <CardHeader className={classes.cardHeader} title="View your BAA" />
      <CardContent className={classes.cardContent}>
        <Typography component="p" variant="body1">
          <Link style={{ cursor: "pointer" }} onClick={onClickDownload}>
            Download your BAA
          </Link>{" "}
          in order to view the contents.
        </Typography>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClickUpload}
          size="small"
          title="Upload a new BAA"
        >
          Upload new BAA
          <UploadToCloudIcon className="ml-1" />
        </Button>
      </CardActions>
    </Card>
  );
}

type UploadBAACardProps = {
  onClickUpload: () => void;
  title: string;
  description: string;
};

function UploadBAACard({
  onClickUpload,
  title,
  description,
}: UploadBAACardProps) {
  const classes = useStyles();

  return (
    <Card>
      <CardHeader className={classes.cardHeader} title={title} />
      <CardContent className={classes.cardContent}>
        <Typography component="p" variant="body1">
          {description}
        </Typography>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClickUpload}
          size="small"
          title="Upload BAA"
        >
          Upload BAA
          <FileUploadIcon className="ml-1" />
        </Button>
      </CardActions>
    </Card>
  );
}

function NoBAASupportCard() {
  const classes = useStyles();

  return (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        title="You cannot sign a BAA with this provider"
      />
      <CardContent className={classes.cardContent}>
        <Typography component="p" variant="body1">
          Please choose another method to resolve this tracker.
        </Typography>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Link href="https://support.freshpaint.io/">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            title="Contact Freshpaint"
          >
            Contact Freshpaint
          </Button>
        </Link>
      </CardActions>
    </Card>
  );
}

export type BAACardProps = {
  siteDomain: string;
  trackerDomain: string;
  onClickUpload: () => void;
  supportsBAA: WebTrackerSupportsBAA;
};

function BAACard({
  siteDomain,
  trackerDomain,
  onClickUpload,
  supportsBAA,
}: BAACardProps) {
  const classes = useStyles();

  const { isLoading: baaIsLoading, isSuccess, data } = useFetchBAA(
    siteDomain,
    trackerDomain
  );

  const baa = data?.webTrackerBAA;

  if (baaIsLoading) {
    return (
      <div className={classes.loaderWrapper}>
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (isSuccess) {
    if (baa) {
      return (
        <ViewBAACard
          onClickDownload={() => window.open(data.downloadLink)}
          onClickUpload={onClickUpload}
        />
      );
    }

    switch (supportsBAA) {
      case "no":
        return <NoBAASupportCard />;
      case "yes":
        return (
          <UploadBAACard
            onClickUpload={onClickUpload}
            title="You can sign a BAA with this provider"
            description="Sign a BAA with this provider and upload it to Freshpaint."
          />
        );
      default:
        return (
          <UploadBAACard
            onClickUpload={onClickUpload}
            title="You may be able to sign a BAA with this provider"
            description="Please reach out to the provider to confirm whether you can sign a BAA with them."
          />
        );
    }
  }

  return <></>;
}

export default BAACard;
